import { Capacitor } from '@capacitor/core';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { matchPath } from 'react-router-dom';
import { AppAsyncThunkAPI, AppState } from '..';
import { callEndpoint } from '../../backend';
import { loadRemoteTripIntoActiveTrip, stopTrip } from '../../log-trips';
import { backgroundGeolocationStart } from '../../log-trips/background-geolocation';
import { definitions, emit } from '../../message-broker';
import { selectCurrentMemberId } from './backend-service';

interface LogTripsState {
  gpsAvailable: boolean;
  physicalActivityPermissionAllowed: boolean;
  currentTripID: number | null;
  currentTripStartTime: string | null;
  currentSpeed: number;
  currentDistance: number;
  activeTrip: boolean;
  tripPaused: boolean;
  followModeActive: boolean;
  followMode: string;
  activeTripHidden: boolean;
  tripLoggedOn: boolean;
  currentTripPlanID: number | null;
  showingTripPlanPoints: boolean;
  tripFloatPlanToken: string | null;
  currentPositionMarkerHidden: boolean;
}

export const initialState: LogTripsState = {
  // Trips config
  gpsAvailable: false,
  physicalActivityPermissionAllowed: false,

  // Current trip stats details
  currentTripID: null,
  currentTripStartTime: null,
  currentSpeed: 0,
  currentDistance: 0,

  activeTrip: false, // Indicates a currently active trip and we are recording trip data
  tripPaused: false, // The currently active trip been paused and is NOT recording trip data
  followModeActive: false, // Indicates follow mode is active and we are updating the map position
  followMode: 'follow', // [ 'follow' | 'north-follow'] indicates the last selected follow mode

  // This is used when there is an active trip and we want to temporarily hide that loaded trip
  //  on the map without the need to reload it when it is needed to be visible again
  activeTripHidden: true,

  // Log on/off and trip plan
  tripLoggedOn: false,
  currentTripPlanID: null,
  showingTripPlanPoints: false,

  // trip float-plan
  tripFloatPlanToken: null,

  // TOD0: move to app-control >> leaving until after initial testing of trips
  //  maybe do as a separate ticket
  currentPositionMarkerHidden: false,
};

// ------------------------------------
// Constants
// ------------------------------------

const LOG_TRIPS_SET_ACTIVE_TRIP = 'LOG_TRIPS_SET_ACTIVE_TRIP';
const LOG_TRIPS_CLEAR_ACTIVE_TRIP = 'LOG_TRIPS_CLEAR_ACTIVE_TRIP';

const LOG_TRIPS_SET_CURRENT_TRIP_ID = 'LOG_TRIPS_SET_CURRENT_TRIP_ID';

const LOG_TRIPS_SET_CURRENT_TRIP_STATISTICS =
  'LOG_TRIPS_SET_CURRENT_TRIP_STATISTICS';

const LOG_TRIPS_SET_CURRENT_TRIP_START_TIME =
  'LOG_TRIPS_SET_CURRENT_TRIP_START_TIME';

const LOG_TRIPS_FOLLOW_MODE = 'LOG_TRIPS_FOLLOW_MODE';
const LOG_TRIPS_SET_FOLLOW_MODE_ACTIVE = 'LOG_TRIPS_SET_FOLLOW_MODE_ACTIVE';
const LOG_TRIPS_CLEAR_FOLLOW_MODE_ACTIVE = 'LOG_TRIPS_CLEAR_FOLLOW_MODE_ACTIVE';

const LOG_TRIPS_SET_PAUSE_TRIP = 'LOG_TRIPS_SET_PAUSE_TRIP';
const LOG_TRIPS_CLEAR_PAUSE_TRIP = 'LOG_TRIPS_CLEAR_PAUSE_TRIP';

const LOG_TRIPS_SET_GPS_AVAILABLE = 'LOG_TRIPS_SET_GPS_AVAILABLE';
const LOG_TRIPS_CLEAR_GPS_AVAILABLE = 'LOG_TRIPS_CLEAR_GPS_AVAILABLE';

const LOG_TRIPS_SET_PHYSICAL_ACTIVITY_ALLOWED =
  'LOG_TRIPS_SET_PHYSICAL_ACTIVITY_ALLOWED';
const LOG_TRIPS_SET_PHYSICAL_ACTIVITY_DENIED =
  'LOG_TRIPS_SET_PHYSICAL_ACTIVITY_DENIED';

const LOG_TRIPS_SET_TRIP_HIDDEN = 'LOG_TRIPS_SET_TRIP_HIDDEN';
const LOG_TRIPS_CLEAR_TRIP_HIDDEN = 'LOG_TRIPS_CLEAR_TRIP_HIDDEN';

const LOG_TRIPS_SET_CURRENT_POSITION_MARKER_HIDDEN =
  'LOG_TRIPS_SET_CURRENT_POSITION_MARKER_HIDDEN';
const LOG_TRIPS_CLEAR_CURRENT_POSITION_MARKER_HIDDEN =
  'LOG_TRIPS_CLEAR_CURRENT_POSITION_MARKER_HIDDEN';

const LOG_TRIPS_SET_TRIP_LOGGED_ON = 'LOG_TRIPS_SET_TRIP_LOGGED_ON';
const LOG_TRIPS_SET_TRIP_LOGGED_OFF = 'LOG_TRIPS_SET_TRIP_LOGGED_OFF';

const LOG_TRIPS_SET_CURRENT_TRIP_PLAN_ID = 'LOG_TRIPS_SET_CURRENT_TRIP_PLAN_ID';
const LOG_TRIPS_SHOW_TRIP_PLAN_POINTS = 'LOG_TRIPS_SHOW_TRIP_PLAN_POINTS';

const LOG_TRIPS_SET_FLOAT_PLAN_TOKEN = 'LOG_TRIPS_SET_FLOAT_PLAN_TOKEN';

// ------------------------------------
// Actions
// ------------------------------------

export const setActiveTrip = () => ({
  type: LOG_TRIPS_SET_ACTIVE_TRIP,
  payload: {
    activeTrip: true,
  },
});

export const clearActiveTrip = () => ({
  type: LOG_TRIPS_CLEAR_ACTIVE_TRIP,
  payload: {
    activeTrip: false,
  },
});

export const setPauseTrip = () => ({
  type: LOG_TRIPS_SET_PAUSE_TRIP,
  payload: {
    tripPaused: true,
  },
});

export const clearPauseTrip = () => ({
  type: LOG_TRIPS_CLEAR_PAUSE_TRIP,
  payload: {
    tripPaused: false,
  },
});

export const setCurrentTripStatistics = (
  speed?: number,
  distance?: number,
) => ({
  type: LOG_TRIPS_SET_CURRENT_TRIP_STATISTICS,
  payload: {
    currentSpeed: speed,
    currentDistance: distance,
  },
});

export const setCurrentTripStartTime = (startTimestamp?: string) => ({
  type: LOG_TRIPS_SET_CURRENT_TRIP_START_TIME,
  payload: {
    currentTripStartTime: startTimestamp,
  },
});

export const setFollowMode = (followMode?: string) => ({
  type: LOG_TRIPS_FOLLOW_MODE,
  payload: {
    followMode: followMode || 'follow',
  },
});

export const setFollowModeActive = () => ({
  type: LOG_TRIPS_SET_FOLLOW_MODE_ACTIVE,
  payload: {
    followModeActive: true,
  },
});
export const clearFollowModeActive = () => ({
  type: LOG_TRIPS_CLEAR_FOLLOW_MODE_ACTIVE,
  payload: {
    followModeActive: false,
  },
});

export const setGpsAvailable = () => ({
  type: LOG_TRIPS_SET_GPS_AVAILABLE,
  payload: {
    gpsAvailable: true,
  },
});

export const clearGpsAvailable = () => ({
  type: LOG_TRIPS_CLEAR_GPS_AVAILABLE,
  payload: {
    gpsAvailable: false,
  },
});

export const setPhysicalActivityAllowed = () => ({
  type: LOG_TRIPS_SET_PHYSICAL_ACTIVITY_ALLOWED,
  payload: {
    physicalActivityPermissionAllowed: true,
  },
});

export const setPhysicalActivityDenied = () => ({
  type: LOG_TRIPS_SET_PHYSICAL_ACTIVITY_DENIED,
  payload: {
    physicalActivityPermissionAllowed: false,
  },
});

export const setActiveTripHidden = () => ({
  type: LOG_TRIPS_SET_TRIP_HIDDEN,
  payload: {
    activeTripHidden: true,
  },
});

export const clearActiveTripHidden = () => ({
  type: LOG_TRIPS_CLEAR_TRIP_HIDDEN,
  payload: {
    activeTripHidden: false,
  },
});

export const setCurrentPositionMarkerHidden = () => ({
  type: LOG_TRIPS_SET_CURRENT_POSITION_MARKER_HIDDEN,
  payload: {
    currentPositionMarkerHidden: true,
  },
});

export const clearCurrentPositionMarkerHidden = () => ({
  type: LOG_TRIPS_CLEAR_CURRENT_POSITION_MARKER_HIDDEN,
  payload: {
    currentPositionMarkerHidden: false,
  },
});

export const setTripLoggedOn = () => ({
  type: LOG_TRIPS_SET_TRIP_LOGGED_ON,
  payload: {
    tripLoggedOn: true,
  },
});

export const setTripLoggedOff = () => ({
  type: LOG_TRIPS_SET_TRIP_LOGGED_OFF,
  payload: {
    tripLoggedOn: false,
  },
});

export const setCurrentTripID = (tripID?: number) => ({
  type: LOG_TRIPS_SET_CURRENT_TRIP_ID,
  payload: {
    currentTripID: tripID,
  },
});

export const setCurrentTripPlanID = (tripPlanID?: number) => ({
  type: LOG_TRIPS_SET_CURRENT_TRIP_PLAN_ID,
  payload: {
    currentTripPlanID: tripPlanID,
  },
});

export const showTripPlanPoints = () => ({
  type: LOG_TRIPS_SHOW_TRIP_PLAN_POINTS,
  payload: {
    showingTripPlanPoints: true,
  },
});

export const setTripFloatPlanToken = (token?: string) => ({
  type: LOG_TRIPS_SET_FLOAT_PLAN_TOKEN,
  payload: {
    tripFloatPlanToken: token,
  },
});

export const actions = {
  setActiveTrip,
  clearActiveTrip,
  setCurrentTripID,
  setCurrentTripPlanID,
  setCurrentTripStatistics,
  setCurrentTripStartTime,
  setFollowModeActive,
  clearFollowModeActive,
  setFollowMode,
  setPauseTrip,
  clearPauseTrip,
  setGpsAvailable,
  clearGpsAvailable,
  setPhysicalActivityAllowed,
  setPhysicalActivityDenied,
  setActiveTripHidden,
  clearActiveTripHidden,
  setCurrentPositionMarkerHidden,
  clearCurrentPositionMarkerHidden,
  setTripLoggedOn,
  setTripLoggedOff,
  showTripPlanPoints,
  setTripFloatPlanToken,
};

// ------------------------------------
// Action Handlers
// ------------------------------------
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const ACTION_HANDLERS = {
  [LOG_TRIPS_SET_ACTIVE_TRIP]: (state: LogTripsState, action: any) => ({
    ...state,
    ...action.payload,
  }),
  [LOG_TRIPS_CLEAR_ACTIVE_TRIP]: (state: LogTripsState, action: any) => ({
    ...state,
    ...action.payload,
  }),
  [LOG_TRIPS_SET_CURRENT_TRIP_STATISTICS]: (
    state: LogTripsState,
    action: any,
  ) => ({
    ...state,
    ...action.payload,
  }),
  [LOG_TRIPS_SET_CURRENT_TRIP_START_TIME]: (
    state: LogTripsState,
    action: any,
  ) => ({
    ...state,
    ...action.payload,
  }),
  [LOG_TRIPS_FOLLOW_MODE]: (state: LogTripsState, action: any) => ({
    ...state,
    ...action.payload,
  }),
  [LOG_TRIPS_SET_FOLLOW_MODE_ACTIVE]: (state: LogTripsState, action: any) => ({
    ...state,
    ...action.payload,
  }),
  [LOG_TRIPS_CLEAR_FOLLOW_MODE_ACTIVE]: (
    state: LogTripsState,
    action: any,
  ) => ({
    ...state,
    ...action.payload,
  }),
  [LOG_TRIPS_SET_PAUSE_TRIP]: (state: LogTripsState, action: any) => ({
    ...state,
    ...action.payload,
  }),
  [LOG_TRIPS_CLEAR_PAUSE_TRIP]: (state: LogTripsState, action: any) => ({
    ...state,
    ...action.payload,
  }),
  [LOG_TRIPS_SET_GPS_AVAILABLE]: (state: LogTripsState, action: any) => ({
    ...state,
    ...action.payload,
  }),
  [LOG_TRIPS_CLEAR_GPS_AVAILABLE]: (state: LogTripsState, action: any) => ({
    ...state,
    ...action.payload,
  }),
  [LOG_TRIPS_SET_PHYSICAL_ACTIVITY_ALLOWED]: (
    state: LogTripsState,
    action: any,
  ) => ({
    ...state,
    ...action.payload,
  }),
  [LOG_TRIPS_SET_PHYSICAL_ACTIVITY_DENIED]: (
    state: LogTripsState,
    action: any,
  ) => ({
    ...state,
    ...action.payload,
  }),
  [LOG_TRIPS_SET_TRIP_HIDDEN]: (state: LogTripsState, action: any) => ({
    ...state,
    ...action.payload,
  }),
  [LOG_TRIPS_CLEAR_TRIP_HIDDEN]: (state: LogTripsState, action: any) => ({
    ...state,
    ...action.payload,
  }),
  [LOG_TRIPS_SET_CURRENT_POSITION_MARKER_HIDDEN]: (
    state: LogTripsState,
    action: any,
  ) => ({
    ...state,
    ...action.payload,
  }),
  [LOG_TRIPS_CLEAR_CURRENT_POSITION_MARKER_HIDDEN]: (
    state: LogTripsState,
    action: any,
  ) => ({
    ...state,
    ...action.payload,
  }),
  [LOG_TRIPS_SET_CURRENT_POSITION_MARKER_HIDDEN]: (
    state: LogTripsState,
    action: any,
  ) => ({
    ...state,
    ...action.payload,
  }),
  [LOG_TRIPS_CLEAR_CURRENT_POSITION_MARKER_HIDDEN]: (
    state: LogTripsState,
    action: any,
  ) => ({
    ...state,
    ...action.payload,
  }),
  [LOG_TRIPS_SET_TRIP_LOGGED_ON]: (state: LogTripsState, action: any) => ({
    ...state,
    ...action.payload,
  }),
  [LOG_TRIPS_SET_TRIP_LOGGED_OFF]: (state: LogTripsState, action: any) => ({
    ...state,
    ...action.payload,
  }),
  [LOG_TRIPS_SET_CURRENT_TRIP_ID]: (state: LogTripsState, action: any) => ({
    ...state,
    ...action.payload,
  }),
  [LOG_TRIPS_SET_CURRENT_TRIP_PLAN_ID]: (
    state: LogTripsState,
    action: any,
  ) => ({
    ...state,
    ...action.payload,
  }),
  [LOG_TRIPS_SHOW_TRIP_PLAN_POINTS]: (state: LogTripsState, action: any) => ({
    ...state,
    ...action.payload,
  }),
  [LOG_TRIPS_SET_FLOAT_PLAN_TOKEN]: (state: LogTripsState, action: any) => ({
    ...state,
    ...action.payload,
  }),
};

// ------------------------------------
// Reducer
// ------------------------------------
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const reducer = (state = initialState, action: any) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const handler =
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    ACTION_HANDLERS[action.type];

  return handler ? handler(state, action) : state;
};

export default reducer;

// ------------------------------------
// Selectors
// ------------------------------------

// Indicates if trip mode is active and we are currently viewing an active trip
export const selectTripModeActive = () =>
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  matchPath(window.appHistory.location.pathname, {
    path: '/maps/trips/active',
    exact: true,
    strict: true,
  })?.isExact || false;

export const loadActiveTrip = createAsyncThunk<void, void, AppAsyncThunkAPI>(
  '[log-trips] load active trip',
  async (_, { getState }) => {
    const state: AppState = getState();

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { activeTrip, currentTripID } = state.logTrips;
    const memberId = selectCurrentMemberId(state);

    // Get the current user from the store
    if (Capacitor.isNativePlatform() && memberId) {
      const trip = await callEndpoint(callEndpoint.endpoints.getActiveTrip, {});

      const tripInApp = activeTrip && currentTripID;
      const tripInBackend = trip && trip.id;
      // If there is no active trip in the frontend, but there is in the backend...
      if (!tripInApp && tripInBackend) {
        await backgroundGeolocationStart({
          tripID: trip && trip.id,
          lowFrequencyTracking: false,
          force: true,
        });
        await loadRemoteTripIntoActiveTrip(trip.id);
      } else if (tripInApp && !tripInBackend) {
        await stopTrip();
        emit(definitions.map.logTripAutomaticallyEnded, {
          tripId: currentTripID,
        });
      }
    }
  },
);
